<template>
    <FunctionalCalendar
        v-model="calendar_data"
        :configs="calendar_config"
        @click.native="openCalendar"
        @dayClicked="preventOpeningCalendar"
        @closed="emitData"
        ref="calendar">
        <template v-slot:dateRangeInputs="props">
            <div class="tw-rounded-lg tw-p-5 tw-flex tw-items-center tw-cursor-pointer" style="height: 49px;" :class="[{'tw-bg-white tw-shadow-card': background === 'white'},{'tw-bg-input': background === 'gray'}]">
                <i class="far fa-calendar"></i>
                <div class="tw-flex tw-items-center" v-if="typeof calendar_data.dateRange !== 'undefined' || (display_initial_values && startDate !== '')">
                    <span class="tw-ml-2 tw-mr-1">{{ display_initial_values ? formattedInitialStartDate : calendar_data.dateRange.start }}</span>
                    <template v-if="display_initial_values ? (formattedInitialEndDate !== '' && formattedInitialEndDate !== formattedInitialStartDate) : (calendar_data.dateRange.end !== '' && calendar_data.dateRange.end !== calendar_data.dateRange.start)">
                        - <span class="tw-ml-1">{{ display_initial_values ? formattedInitialEndDate : calendar_data.dateRange.end }}</span>
                    </template>
                </div>
                <template v-else>
                    <div class="tw-bg-gray-0 tw-rounded-lg tw-py-2 tw-ml-2 tw-mr-2" style="width: 60px;"></div>
                    <div class="tw-bg-gray-0 tw-rounded-lg tw-py-2" style="width: 60px;"></div>
                </template>
            </div>
        </template>
    </FunctionalCalendar>
</template>

<script>
    import { FunctionalCalendar } from 'vue-functional-calendar';
    import moment from 'moment';

    export default {
        name: 'DateRangePicker',

        components: {
            FunctionalCalendar,
        },

        props: {
            startDate: {
                default: '',
            },

            endDate: {
                default: '',
            },

            format: {
                default: 'DD-MM-YYYY HH:mm',
            },

            background: {
                default: 'white'
            },
        },

        watch: {
            startDate(val) {
                if (moment(val, 'MMMM DD, YYYY HH:mm', true).isValid()) {
                   this.format_type_date_props = 'MMMM DD, YYYY HH:mm';
                } else if (moment(val, 'YYYY-MM-DD HH:mm', true).isValid()) {
                    this.format_type_date_props = 'YYYY-MM-DD HH:mm'
                } else if (moment(val, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
                    this.format_type_date_props = 'YYYY-MM-DD HH:mm:ss'
                }
            },
        },

        computed: {
            formattedInitialStartDate() {
                if (this.startDate !== '') {
                    return moment(this.startDate, this.format_type_date_props).format('DD-MM-YYYY');
                }

                return '';
            },

            formattedInitialEndDate() {
                if (this.endDate !== '' && this.startDate !== '') {
                    if (moment(this.startDate, this.format_type_date_props).format('DD-MM-YYYY') !== moment(this.endDate, this.format_type_date_props).format('DD-MM-YYYY')) {
                        return moment(this.endDate, this.format_type_date_props).format('DD-MM-YYYY');
                    }
                }

                return '';
            },
        },

        data() {
            return {
                format_type_date_props: 'MMMM DD, YYYY HH:mm',
                display_initial_values: true,
                prevent_opening_calendar: false,
                calendar_data: {},
                calendar_config: {
                    sundayStart: true,
                    dateFormat: this.format.toLowerCase(),
                    isDateRange: true,
                    changeMonthFunction: true,
                    changeYearFunction: true,
                    isAutoCloseable: true,
                    isModal: true,
                },
            };
        },

        methods: {
            // theoretic function to show the currently selected day, but sadly that doesn't work yet in the library, it's a common issue
            setupInitialDateRange() {
                // if we have a previously stored date range
                if (this.startDate !== '') {
                    // manually init the calendar so we can set the date range after
                    this.$refs.calendar.ChooseDate('today');

                    // update the date range to values stored in the db
                    this.$nextTick(() => {
                        this.calendar_data.dateRange = {};

                        if (this.startDate !== '') {
                            this.calendar_data.dateRange.start = moment(this.startDate).format(this.format);

                            if (this.endDate !== '') {
                                this.calendar_data.dateRange.end = moment(this.endDate).format(this.format);
                            }
                        }
                    });
                }
            },

            // hacky way to allow the automatic closing of the calendar but "fixes" an internal bug of the library where, once automatically closed, the calendar could not be opened again until somewhere else on the page was clicked
            openCalendar() {
                if (!this.prevent_opening_calendar) {
                    this.$refs.calendar.showCalendar = true;
                }
            },

            preventOpeningCalendar() {
                this.display_initial_values = false;
                this.prevent_opening_calendar = true;

                setTimeout(() => {
                    this.prevent_opening_calendar = false;
                }, 0);
            },

            // emit the current selected data
            emitData() {
                if (typeof this.calendar_data.dateRange !== 'undefined' && this.calendar_data.dateRange.start !== '') {
                    let current_start_date;

                    // add the hours and minutes back into the start date
                    if (this.startDate !== '') {
                        const initial_start_date = moment(this.startDate, 'MMMM DD, YYYY HH:mm').format('HH:mm');
                        current_start_date = moment(this.calendar_data.dateRange.start, this.format).toDate();
                        current_start_date.setHours(moment(initial_start_date, 'HH:mm').hours());
                        current_start_date.setMinutes(moment(initial_start_date, 'HH:mm').minutes());
                        current_start_date = moment(current_start_date).format(this.format);
                        
                    } else {
                        current_start_date = moment(this.calendar_data.dateRange.start, this.format).format(this.format);
                    }

                    let current_end_date = current_start_date;

                    // add the hours and minutes back into the end date
                    if (this.endDate !== '') {
                        const initial_end_date = moment(this.endDate, 'MMMM DD, YYYY HH:mm').format('HH:mm');
                        current_end_date = moment(this.calendar_data.dateRange.end, this.format).toDate();
                        current_end_date.setHours(moment(initial_end_date, 'HH:mm').hours());
                        current_end_date.setMinutes(moment(initial_end_date, 'HH:mm').minutes());
                        current_end_date = moment(current_end_date).format(this.format);
                    } else if (this.calendar_data.dateRange.end !== '' && (this.calendar_data.dateRange.end !== this.calendar_data.dateRange.start)) {
                        current_end_date = moment(this.calendar_data.dateRange.end, this.format).format(this.format);
                    }
                    
                    this.$emit('closed', {
                        start_date: moment(current_start_date, this.format).format(this.format),
                        end_date: moment(current_end_date, this.format).format(this.format),
                    });
                }
            },
        },
    };
</script>

<style lang="scss">
    @import "~vue-functional-calendar/src/assets/scss/calendar.scss";
    @import "~vue-functional-calendar/src/assets/scss/variables.scss";

    .vfc-main-container {
        background: none;
        box-shadow: none;

        .vfc-top-date {
            margin-top: 22px;

            span {
                &.vfc-underline {
                    text-decoration: none;

                    &:hover {
                        @apply tw-opacity-50;
                    }
                }
            }
        }

        .vfc-dayNames {
            .vfc-day {
                @apply tw-font-semibold;
            }
        }

        .vfc-week {
            .vfc-span-day {
                @apply tw-cursor-pointer;

                &.vfc-hover {
                    &:hover {
                        @apply tw-bg-gray-0 #{!important};
                    }
                }
            }
        }

        .vfc-separately-navigation-buttons {
            .vfc-cursor-pointer {
                &:hover {
                    @apply tw-opacity-50;
                }
            }
        }

        &:not(.vfc-modal) {
            .vfc-calendars-container {
                overflow: visible;
            }

            .vfc-top-date {
                font-size: 15px;
                font-weight: 600;
                margin-top: 0;
                margin-bottom: 15px;
            }

            .vfc-separately-navigation-buttons {
                div {
                    margin-top: 7px;
                    margin-left: 2px;
                    margin-right: 2px;
                    margin-bottom: 15px;

                    &.vfc-arrow-left {
                        transform: scale(0.8) rotate(-45deg);
                    }

                    &.vfc-arrow-right {
                        transform: scale(0.8) rotate(45deg);
                    }
                }
            }

            .vfc-calendar {
                .vfc-content {
                    margin: 0;
                }
            }

            .vfc-dayNames, .vfc-week {
                width: calc(100% + 16px);
                margin-left: -8px;
            }

            .vfc-week {
                .vfc-span-day {
                    @apply tw-cursor-pointer;

                    &.vfc-hover {
                        &:hover {
                            @apply tw-bg-gray-200 #{!important};
                        }
                    }

                    &.vfc-marked {
                        &:hover {
                            background-color: #66b3cc !important;
                        }
                    }
                }
            }
        }

        &.vfc-modal {
            @apply tw-bg-white;
            box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.25);
            width: 300px !important;
            margin-left: -30px;
            margin-top: 10px;
        }
    }
</style>